import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import { ToastService } from '@vsolv/vectors-ui/alert';

enum PageState {
  LOADING = 'LOADING',
  READY = 'READY',
  EXCHANGING = 'EXCHANGING',
  ERROR = 'ERROR',
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-auth-continue',
  templateUrl: './auth-continue.page.html',
  standalone: false,
})
export class AuthContinuePage implements OnInit {
  pageState = PageState.LOADING;
  readonly PageState = PageState;

  email = '';
  token = '';
  redirect = '';
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private firebaseSvc: FirebaseService,
    private toastSvc: ToastService
  ) {}

  async ngOnInit() {
    // Extract query params
    const params = this.route.snapshot.queryParams;
    this.email = params['email'] || '';
    this.token = params['vsolvToken'] || '';
    this.redirect = params['redirect'] || '';

    // Validate required params
    if (!this.email || !this.token) {
      this.pageState = PageState.ERROR;
      this.errorMessage = 'Invalid login link. The required parameters are missing.';
      return;
    }

    // Ready to continue
    this.pageState = PageState.READY;
  }

  async continueToPortal() {
    this.pageState = PageState.EXCHANGING;

    try {
      // Exchange the token for Firebase magic link
      const result = await this.firebaseSvc.exchangeLoginToken(this.email, this.token);

      if (!result || !result.firebaseMagicLink) {
        throw new Error('Failed to retrieve login link');
      }

      console.log(
        JSON.stringify({
          logName: 'vsolv.authdiag',
          severity: 'INFO',
          event: 'token_exchange_success',
        })
      );

      // Redirect to Firebase magic link
      window.location.href = result.firebaseMagicLink;
    } catch (error: unknown) {
      console.error('Token exchange failed:', error);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorStatus = (error as { status?: number })?.status;

      console.log(
        JSON.stringify({
          logName: 'vsolv.authdiag',
          severity: 'ERROR',
          event: 'token_exchange_failure',
          error: errorMessage,
        })
      );

      this.pageState = PageState.ERROR;

      // Determine error message based on response
      if (errorStatus === 404 || errorStatus === 400) {
        this.errorMessage = 'This login link is invalid, expired, or has already been used.';
      } else {
        this.errorMessage = 'An unexpected error occurred. Please try again.';
      }

      this.toastSvc.show({
        type: 'error',
        title: 'Login Failed',
        text: this.errorMessage,
      });
    }
  }

  async requestNewLink() {
    try {
      await this.firebaseSvc.sendProtectedLoginEmail(this.email, this.redirect || undefined);
      this.toastSvc.show({
        type: 'success',
        title: 'Email Sent',
        text: 'A new login link has been sent to your email address.',
      });
    } catch (error) {
      console.error('Failed to send new login link:', error);
      this.toastSvc.show({
        type: 'error',
        title: 'Error',
        text: 'Failed to send a new login link. Please try again.',
      });
    }
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }
}
